import React, { useEffect, useState } from "react";

interface CountrySupport {
  code: string;
  name: string;
  ibanValidationSupported: boolean;
  bankInfoSupported: boolean;
}

const SupportedCountriesPage = () => {
  const [countries, setCountries] = useState<CountrySupport[]>([]);
  const [activeTab, setActiveTab] = useState<"validation" | "bankinfo">("validation");

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch("/api/v1/public/supported-countries");
        const data = await response.json();
        setCountries(data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const validationCountries = countries.filter((c) => c.ibanValidationSupported);
  const bankInfoCountries = countries.filter((c) => c.bankInfoSupported);

  const renderCountryCards = (countryList: CountrySupport[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
      {countryList.map((country) => (
        <div
          key={country.code}
          className="bg-gray-50 p-4 rounded border shadow-sm hover:shadow-md transition"
        >
          <h3 className="text-gray-800 font-semibold">{country.name}</h3>
          <p className="text-gray-500 text-sm">Code: {country.code}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-center text-teal-700 mb-8">Supported Countries</h1>

      <div className="flex justify-center mb-6 space-x-4">
        <button
          className={`px-4 py-2 rounded font-medium border transition ${
            activeTab === "validation" ? "bg-teal-600 text-white" : "bg-white text-gray-700 border-gray-300"
          }`}
          onClick={() => setActiveTab("validation")}
        >
          IBAN Validation
        </button>
        <button
          className={`px-4 py-2 rounded font-medium border transition ${
            activeTab === "bankinfo" ? "bg-teal-600 text-white" : "bg-white text-gray-700 border-gray-300"
          }`}
          onClick={() => setActiveTab("bankinfo")}
        >
          Bank Info
        </button>
      </div>

      {activeTab === "validation" && (
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">✅ IBAN Validation Support</h2>
          <p className="mb-4 text-gray-600">
            The IBAN Analyzer API supports country-specific IBAN validation rules for the following countries:
          </p>
          {renderCountryCards(validationCountries)}
        </section>
      )}

      {activeTab === "bankinfo" && (
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">🏦 Bank Info Support</h2>
          <p className="mb-4 text-gray-600">
            The IBAN Analyzer API supports detailed bank information lookup based on the IBAN for the following countries:
          </p>
          {renderCountryCards(bankInfoCountries)}
        </section>
      )}
    </div>
  );
};

export default SupportedCountriesPage;
